import * as THREE from 'three';

/**
 * Keyboard / joystick flying speed (metres per second).
 *
 * One base speed, changed in flight with `+` / `-` or the toolbar chip, and
 * two fixed modifiers on top of it: Shift boosts, Alt slows for precision
 * work indoors. Until the user picks a speed for a project, the base follows
 * the size of the loaded point clouds — a 5 km corridor and a single room
 * want speeds two orders of magnitude apart.
 */
export const FLY_SPEED_MIN = 0.25;
export const FLY_SPEED_MAX = 500;
export const FLY_SPEED_DEFAULT = 10;
/** One `+` / `-` press multiplies / divides the speed by this. */
export const FLY_SPEED_STEP = 1.5;
export const FLY_SPEED_BOOST = 4;
export const FLY_SPEED_PRECISION = 0.25;

/** Seconds to fly across the scene's bounding-box diagonal at the auto speed. */
const AUTO_CROSSING_SECONDS = 60;
const AUTO_MIN = 1;
const AUTO_MAX = 150;

export const clampFlySpeed = (v: number) => Math.min(FLY_SPEED_MAX, Math.max(FLY_SPEED_MIN, v));

export const stepFlySpeed = (v: number, dir: 1 | -1) =>
  clampFlySpeed(dir > 0 ? v * FLY_SPEED_STEP : v / FLY_SPEED_STEP);

/** Base speed derived from the scene size; the old fixed 10 m/s with nothing to measure. */
export const autoFlySpeed = (diagonalM: number | null) => {
  if (diagonalM == null || !Number.isFinite(diagonalM) || diagonalM <= 0) return FLY_SPEED_DEFAULT;
  return Math.min(AUTO_MAX, Math.max(AUTO_MIN, diagonalM / AUTO_CROSSING_SECONDS));
};

/** The modifiers multiply, so Shift+Alt cancels out to the base speed. */
export const flySpeedMultiplier = (boost: boolean, precision: boolean) =>
  (boost ? FLY_SPEED_BOOST : 1) * (precision ? FLY_SPEED_PRECISION : 1);

/**
 * W / S in ortho zoom instead of flying: the view looks straight down, so
 * moving along it changed nothing on screen. Zoom is scale-free, so it runs at
 * a fixed rate (×2 per second) under the same Shift / Alt modifiers rather
 * than the metres-per-second fly speed.
 */
export const ORTHO_KEY_ZOOM_PER_SECOND = 2;

/** Factor to multiply the ortho zoom by this frame; `dir` +1 = W (in), -1 = S (out). */
export const orthoKeyZoomFactor = (dir: number, multiplier: number, delta: number) =>
  Math.pow(ORTHO_KEY_ZOOM_PER_SECOND, dir * multiplier * delta);

/**
 * Ortho panning (A / D) is measured in screens, not metres: a fixed m/s that
 * suits the whole site jumps a full screen per frame when zoomed in to
 * centimetres. At the auto speed the view crosses half its height per second
 * at any zoom; a speed the user picked is kept as its ratio to the auto speed.
 */
export const ORTHO_SCREENS_PER_SECOND = 0.5;

/** Ortho pan speed in m/s for a visible view `viewHeightM` metres tall. */
export const orthoFlySpeed = (speed: number, autoSpeed: number, viewHeightM: number) =>
  (speed / autoSpeed) * ORTHO_SCREENS_PER_SECOND * viewHeightM;

/** Two significant figures at most: "0.25", "1.5", "12", "150". */
export const formatFlySpeed = (v: number) => {
  if (v < 1) return String(Math.round(v * 100) / 100);
  if (v < 10) return String(Math.round(v * 10) / 10);
  return String(Math.round(v));
};

// The chip's slider runs on a log scale so 0.25 m/s and 500 m/s are both
// reachable without the low end collapsing into the first pixel.
const LOG_MIN = Math.log(FLY_SPEED_MIN);
const LOG_SPAN = Math.log(FLY_SPEED_MAX) - LOG_MIN;
export const flySpeedToSlider = (v: number) => (Math.log(clampFlySpeed(v)) - LOG_MIN) / LOG_SPAN;
export const sliderToFlySpeed = (t: number) =>
  clampFlySpeed(Math.exp(LOG_MIN + Math.min(1, Math.max(0, t)) * LOG_SPAN));

type BoundedCloud = Pick<THREE.Object3D, 'matrixWorld' | 'updateMatrixWorld'> & {
  pcoGeometry: { boundingBox: THREE.Box3; tightBoundingBox?: THREE.Box3 | null };
};

/** World-space diagonal of the union of the clouds' bounds, or null with none loaded. */
export const pointCloudsDiagonal = (clouds: readonly BoundedCloud[]) => {
  if (clouds.length === 0) return null;
  const union = new THREE.Box3();
  const box = new THREE.Box3();
  for (const pco of clouds) {
    pco.updateMatrixWorld(true);
    box.copy(pco.pcoGeometry.tightBoundingBox ?? pco.pcoGeometry.boundingBox).applyMatrix4(pco.matrixWorld);
    union.union(box);
  }
  return union.isEmpty() ? null : union.getSize(box.min).length();
};

const storageKey = (projectId: string) => `@actegon.gis.flySpeed.${projectId}`;

/** The speed the user picked for this project, or null to follow the scene. */
export const readStoredFlySpeed = (projectId: string): number | null => {
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    const v = raw == null ? NaN : Number(raw);
    return Number.isFinite(v) && v > 0 ? clampFlySpeed(v) : null;
  } catch {
    return null;
  }
};

export const writeStoredFlySpeed = (projectId: string, v: number | null) => {
  try {
    if (v == null) localStorage.removeItem(storageKey(projectId));
    else localStorage.setItem(storageKey(projectId), String(v));
  } catch {
    // Private window / blocked storage: the speed just won't survive a reload.
  }
};
